import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { WeappsSquare } from './WeappsLogo'

/**
 * Full-screen loading overlay: the square weapps logo "breathing" inside a card over a dim + blurred
 * backdrop. The backdrop also intercepts pointer events, so the screen underneath isn't clickable
 * while loading. Themed via the `.wl-*` classes in styles.css.
 *
 * Rendered through a portal so its `position: fixed` escapes an ancestor that would clip it (e.g. the
 * tab bar's `backdrop-filter`). Target is the phone frame (#app-frame) when present, so on desktop the
 * overlay centers inside the simulated phone rather than covering the whole desk; falls back to <body>.
 *
 * `delayMs` (default 250ms) suppresses the overlay for a short grace period so a fast load — notably the
 * async Persisted Runtime Cache restore on a warm boot — never flashes a spinner before the cached content
 * paints. Genuine waits still show it once the grace period elapses. Pass `delayMs={0}` to show immediately.
 */
export function LoadingOverlay({ label = 'Carregando…', delayMs = 250 }: { label?: string; delayMs?: number }) {
  const [show, setShow] = useState(delayMs <= 0)
  useEffect(() => {
    if (delayMs <= 0) return
    const id = setTimeout(() => setShow(true), delayMs)
    return () => clearTimeout(id)
  }, [delayMs])
  if (!show) return null

  const overlay = (
    <div className="wl-overlay" role="status" aria-live="polite">
      <div className="wl-card">
        <WeappsSquare className="wl-logo" style={{ color: 'var(--txt2)' }} />
        <span className="wl-label">{label}</span>
      </div>
    </div>
  )
  if (typeof document === 'undefined') return overlay
  return createPortal(overlay, document.getElementById('app-frame') ?? document.body)
}
